'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, type ReactNode } from 'react';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { CartStep } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

/**
 * The slim checkout header / footer belong to the steps after the cart and to the confirmation.
 * The step is read with `useSearchParams` (not the stepper's external store), so a reload of
 * `/kosik?cs=…` renders the right chrome on the server; Next keeps it in sync with the stepper's
 * `history.pushState`.
 */
export function useIsCheckoutChrome() {
  const pathname = useLocalePathname();
  const step = useSearchParams().get('cs');
  return (
    (arePathsEqual(pathname, ROUTES.CART()) && !!step && step !== CartStep.DETAIL) ||
    arePathsEqual(pathname, ROUTES.ORDER_CONFIRM())
  );
}

function ChromeSwitch({ checkout, children }: { checkout: ReactNode; children: ReactNode }) {
  return useIsCheckoutChrome() ? checkout : children;
}

/**
 * Chooses between the site header / footer (`children`) and the checkout ones (Figma „Header“
 * 170:14477, „Footer checkout“ 159:39168). Both are rendered on the server. A statically rendered
 * page has no search params – there the site chrome is the fallback.
 */
export function CheckoutChrome({
  checkout,
  children,
}: {
  checkout: ReactNode;
  children: ReactNode;
}) {
  return (
    <Suspense fallback={children}>
      <ChromeSwitch checkout={checkout}>{children}</ChromeSwitch>
    </Suspense>
  );
}
